/**
 * @fileoverview Mock-only sync of user lifecycle changes into the Sales roster and targets.
 * @description Applies each deactivation and reactivation User Management's mock records (see
 * shared/mocks/user-lifecycle-mock.store.ts) to the Sales roster mock: the user turns Inactive or
 * Active, their direct reports move to the recipient, a reactivated user comes back with the role
 * and manager they were given, and a deactivated user's outstanding targets are transferred to the
 * recipient. Without it the roster and the owner pickers built from it would keep listing a
 * deactivated user as Active after the refetch a lifecycle command triggers. Imported for its side
 * effect by the roster mock service, so it is wired whenever the Sales mocks are.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  findSalesUserById,
  reparentDirectReports,
  setSalesUserStatus,
} from '@/features/sales/mocks/sales-roster-mock-store'
import { transferOutstandingTargets } from '@/features/sales/mocks/targets-mock-store'
import { subscribeToUserLifecycleChanges } from '@/shared/mocks/user-lifecycle-mock.store'

import type { TeamRole } from '@/features/sales/types/sales.types'
import type { UserLifecycleChange } from '@/shared/mocks/user-lifecycle-mock.store'

/**
 * @description Applies one lifecycle change to the roster and targets. A user the roster does not
 * hold (a user invited through User Management only) is skipped.
 * @param {UserLifecycleChange} change - The deactivation or reactivation that was applied.
 */
export function applyUserLifecycleChange(change: UserLifecycleChange): void {
  const user = findSalesUserById(change.userId)
  if (!user) {
    return
  }

  if (change.kind === 'DEACTIVATED') {
    if (change.recipientUserId) {
      reparentDirectReports(change.userId, change.recipientUserId)
      transferOutstandingTargets(change.userId, change.recipientUserId, change.actingUserId)
    }
    setSalesUserStatus(change.userId, 'Inactive')
    return
  }

  user.role = change.roleName as TeamRole
  user.reportsTo = change.reportingManagerUserId
  setSalesUserStatus(change.userId, change.invitedAgain ? 'Pending Invitation' : 'Active')
}

subscribeToUserLifecycleChanges(applyUserLifecycleChange)
